import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, LockIcon, SwitchIcon } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { Input } from '../../../../../../../components/input/index';
import { useLocale } from '../../../../../../../hooks/index';
import { MappingEditorStore, VariableMappingType } from '../../../../store/mapping-editor.store';
import { resolveFieldOptions } from '../../../../utils/resolve-field-options.util';

export type VariableMappingFieldProps = {
  mappingType: VariableMappingType;
  onMappingChange: (value: string, type: VariableMappingType) => void;
  value: string;
  varName: string;
};

export const VariableMappingField: FC<VariableMappingFieldProps> = ({
  mappingType,
  onMappingChange,
  value,
  varName,
}) => {
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const templateContext = MappingEditorStore(({ templateContext }) => templateContext);
  const fieldOptions = useMemo(() => resolveFieldOptions(templateContext), [templateContext]);

  const selectedOption = useMemo(
    () => fieldOptions.find((o) => o.value === value),
    [fieldOptions, value],
  );

  const hasValue = value.length > 0;
  const labelText = `{{${varName}}}`;

  if (mappingType === VariableMappingType.CUSTOM) {
    return (
      <div className="flex w-full min-w-0 flex-col rounded-lg border border-gray-100 p-3">
        <span
          className={twMerge(
            'mb-1 min-w-0 text-xs transition-colors duration-200 [overflow-wrap:anywhere]',
            hasValue ? 'text-forest-600' : 'text-gray-700',
          )}
        >
          {labelText}
        </span>
        <Input
          autoFocus
          onChange={(val) => onMappingChange(val, VariableMappingType.CUSTOM)}
          placeholder={messagesLocale['ENTER_TEXT']}
          placeholderClassName={twMerge(
            'opacity-100 transition-all',
            'group-focus-within:opacity-0 group-focus-within:invisible',
            value && 'opacity-0 invisible',
          )}
          value={value}
          wrapperClassName="group"
        />
        <div className="mt-3 px-1">
          <div className="flex items-center gap-1 font-medium text-gray-700">
            <LockIcon className="h-3.5 w-3.5" fillColor="#FEF0C7" />
            <span className="text-[12px] italic">
              <AppFormattedMessage id="CUSTOM_TEXT_VALUE" />
            </span>
          </div>
          <p className="text-[12px] italic text-gray-500">
            <AppFormattedMessage id="CUSTOM_VALUE_SAVED_IN_TEMPLATE" />
          </p>
          <button
            className="mt-2 flex w-full items-center gap-1 border-t border-gray-100 pt-2 text-left text-[12px] font-normal italic text-[#2563EB] underline"
            onClick={() => onMappingChange('', VariableMappingType.SYSTEM)}
            type="button"
          >
            <SwitchIcon className="h-3.5 w-3.5" iconColor="#2563EB" />
            <AppFormattedMessage id="USE_SYSTEM_FIELD_INSTEAD" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col rounded-lg border border-gray-100 p-3">
      <span
        className={twMerge(
          'mb-1 min-w-0 text-[12px] transition-colors duration-200 [overflow-wrap:anywhere]',
          hasValue ? 'text-forest-600' : 'text-gray-700',
        )}
      >
        {labelText}
      </span>

      <Combobox
        onChange={(v) => onMappingChange(v ?? '', VariableMappingType.SYSTEM)}
        value={value}
      >
        {({ open }) => (
          <div className={twMerge('relative w-full overflow-visible')}>
            <ComboboxInput
              className={twMerge(
                'relative z-10 h-10 w-full cursor-pointer rounded-t border-b border-gray-700 bg-gray-50 py-0 pl-2.5 pr-9 text-left text-[14px] outline-none transition-colors',
                open ? 'border-b-2 border-brand-700' : 'hover:border-gray-900',
                selectedOption ? 'text-[#1E293B]' : 'text-[#475569]',
              )}
              displayValue={(item) => {
                if (!item) {
                  return '';
                }
                const opt = fieldOptions.find((o) => o.value === item);
                if (!opt) {
                  return '';
                }
                return opt.labelKeyParams
                  ? messagesLocaleFn(opt.labelKey, opt.labelKeyParams)
                  : messagesLocale[opt.labelKey];
              }}
              placeholder={messagesLocale['CHOOSE']}
              readOnly
            />

            <ComboboxButton
              className="absolute inset-x-0 top-0 z-20 h-10 rounded-t"
              type="button"
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex items-center pr-2.5">
              <ChevronDownIcon
                className={twMerge(
                  'h-4 w-4 transition-transform duration-200',
                  open
                    ? 'rotate-180 text-[#2563EB]'
                    : hasValue
                      ? 'text-green-600'
                      : 'text-[#64748B]',
                )}
              />
            </div>

            {open && (
              <ComboboxOptions
                anchor={{ gap: 4, to: 'bottom start' }}
                className={twMerge(
                  'pointer-events-auto z-[10001] flex flex-col rounded-lg border border-gray-200 bg-white shadow-2xl outline-none',
                  'w-[var(--input-width)]',
                )}
                onMouseEnter={(e) => {
                  e.currentTarget.focus();
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                portal={false}
                static
              >
                <div
                  className="scrollbar-always-visible max-h-[200px] overflow-y-auto overscroll-contain py-1"
                  onWheel={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {fieldOptions.map((opt) => (
                    <ComboboxOption
                      className="cursor-pointer px-4 py-2.5 text-[14px] text-[#334155] data-[focus]:bg-[#F1F5F9] data-[focus]:text-[#1E293B]"
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.labelKeyParams
                        ? messagesLocaleFn(opt.labelKey, opt.labelKeyParams)
                        : messagesLocale[opt.labelKey]}
                    </ComboboxOption>
                  ))}
                </div>

                <div className="border-t border-[#F1F5F9] bg-white p-4">
                  <p className="mb-1 text-[10px] text-[#94A3B8]">
                    <AppFormattedMessage id="NO_MATCHING_FIELD" />
                  </p>
                  <button
                    className="hover:text-blue-800 text-[14px] font-normal text-[#2563EB] underline underline-offset-2"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onMappingChange('', VariableMappingType.CUSTOM);
                    }}
                    type="button"
                  >
                    <AppFormattedMessage id="CUSTOM_TEXT_VALUE" />
                  </button>
                </div>
              </ComboboxOptions>
            )}
          </div>
        )}
      </Combobox>
    </div>
  );
};
